// ---------------------------------------------------------------------------
// RH · Recrutamento & Seleção — constantes compartilhadas (fonte da verdade:
// handoff 0061 §2). Enums do backend + labels pt-BR + helpers derivados.
//
// `stage` e `final_outcome` são READ-ONLY na UI (o backend os muda); estas
// constantes servem só para EXIBIR o estado e para derivar as ações válidas
// (qual rodada agendar, quais results aceitar por rodada, quando a tela é
// somente-exibição).
// ---------------------------------------------------------------------------

// ─── Funil (candidate.stage) ───────────────────────────────────────────────
export const STAGE = {
   BANCO_CURRICULOS: 'BANCO_CURRICULOS',
   ENTREVISTA_1: 'ENTREVISTA_1',
   ENTREVISTA_2: 'ENTREVISTA_2',
   ONBOARDING: 'ONBOARDING',
   ADMITIDO: 'ADMITIDO',
   ENCERRADO: 'ENCERRADO',
};

export const STAGE_LABEL = {
   BANCO_CURRICULOS: 'Banco de currículos',
   ENTREVISTA_1: 'Entrevista 1',
   ENTREVISTA_2: 'Entrevista 2',
   ONBOARDING: 'Onboarding',
   ADMITIDO: 'Admitido',
   ENCERRADO: 'Encerrado',
};

// Stages terminais PARA A ESTEIRA DE ENTREVISTAS: a UI não expõe ações de
// entrevista neles. Desde a Fase 1, ONBOARDING/ADMITIDO deixaram de ser
// só-exibição — abrem a página de onboarding (card na ficha).
export const TERMINAL_STAGES = [
   STAGE.ONBOARDING,
   STAGE.ADMITIDO,
   STAGE.ENCERRADO,
];

export function isTerminalStage(stage) {
   return TERMINAL_STAGES.includes(stage);
}

// Rodada de entrevista associada ao stage atual (ENTREVISTA_1 → 1, _2 → 2).
export function roundForStage(stage) {
   if (stage === STAGE.ENTREVISTA_1) return 1;
   if (stage === STAGE.ENTREVISTA_2) return 2;
   return null;
}

// ─── final_outcome (só quando encerrado/admitido) ──────────────────────────
export const FINAL_OUTCOME_LABEL = {
   REPROVADO: 'Reprovado',
   INCOMPATIVEL: 'Incompatível',
   RECUSA_DE_VAGA: 'Recusa de vaga',
   DESISTIU: 'Desistiu',
   NAO_COMPARECEU: 'Não compareceu',
   DESQUALIFICADO: 'Desqualificado',
   ADMITIDO: 'Admitido',
};

// ─── Modalidade da entrevista ──────────────────────────────────────────────
export const MODALITY = {
   ONLINE: 'ONLINE',
   PRESENCIAL: 'PRESENCIAL',
};

export const MODALITY_LABEL = {
   ONLINE: 'Online',
   PRESENCIAL: 'Presencial',
};

export const MODALITY_OPTIONS = [
   { value: MODALITY.ONLINE, label: MODALITY_LABEL.ONLINE },
   { value: MODALITY.PRESENCIAL, label: MODALITY_LABEL.PRESENCIAL },
];

// Padrão de modalidade por rodada (E1 = ONLINE, E2 = PRESENCIAL).
export function defaultModalityForRound(round) {
   return round === 2 ? MODALITY.PRESENCIAL : MODALITY.ONLINE;
}

// ─── Gênero ────────────────────────────────────────────────────────────────
export const GENDER_OPTIONS = [
   { value: 'MASCULINO', label: 'Masculino' },
   { value: 'FEMININO', label: 'Feminino' },
   { value: 'OUTRO', label: 'Outro' },
];

// ─── Status da vaga (job_opening.status) ───────────────────────────────────
export const JOB_OPENING_STATUS = {
   ABERTA: 'ABERTA',
   CONGELADA: 'CONGELADA',
   ENCERRADA: 'ENCERRADA',
   PREENCHIDA: 'PREENCHIDA',
};

export const JOB_OPENING_STATUS_LABEL = {
   ABERTA: 'Aberta',
   CONGELADA: 'Congelada',
   ENCERRADA: 'Encerrada',
   PREENCHIDA: 'Preenchida',
};

// ─── Resultado da entrevista — SUBCONJUNTO POR RODADA ──────────────────────
// O backend rejeita result fora do subconjunto da rodada (422
// invalid_result_for_round). REMARCADO NÃO é enviado em /result — é o endpoint
// /reschedule; aparece só no histórico.
export const INTERVIEW_RESULT_LABEL = {
   PROXIMA_ETAPA: 'Próxima etapa',
   INCOMPATIVEL: 'Incompatível',
   RECUSA_DE_VAGA: 'Recusa de vaga',
   NAO_COMPARECEU: 'Não compareceu',
   APROVADO: 'Aprovado',
   REPROVADO: 'Reprovado',
   DESISTIU: 'Desistiu',
   REMARCADO: 'Remarcado',
};

export const INTERVIEW_RESULTS_BY_ROUND = {
   1: ['PROXIMA_ETAPA', 'INCOMPATIVEL', 'RECUSA_DE_VAGA', 'NAO_COMPARECEU'],
   2: ['APROVADO', 'REPROVADO', 'DESISTIU', 'NAO_COMPARECEU'],
};

export function resultOptionsForRound(round) {
   return (INTERVIEW_RESULTS_BY_ROUND[round] ?? []).map((value) => ({
      value,
      label: INTERVIEW_RESULT_LABEL[value] ?? value,
   }));
}

// ─── Roteiro estruturado (structured_notes) — campos por rodada ────────────
// Todos opcionais e de texto livre. As chaves espelham o backend (snake_case).
// `hint` = subtítulo de ajuda (o que preencher) para facilitar a aderência de
// usuários novos; campos auto-explicativos ficam sem hint.
export const STRUCTURED_NOTES_FIELDS_BY_ROUND = {
   1: [
      {
         key: 'trajetoria',
         label: 'Trajetória',
         hint: 'Resumo da experiência profissional e formação — de onde vem e o que já fez.',
      },
      {
         key: 'motivacao',
         label: 'Motivação',
         hint: 'O que motiva o candidato a buscar esta vaga e o que ele espera do cargo.',
      },
      {
         key: 'alinhamento_pratico',
         label: 'Alinhamento prático',
         hint: 'Disponibilidade, pretensão salarial, local e outros pontos práticos.',
      },
      { key: 'perguntas', label: 'Perguntas do candidato' },
   ],
   2: [
      {
         key: 'star',
         label: 'STAR (situação · tarefa · ação · resultado)',
         hint: 'Situação, Tarefa, Ação e Resultado de um caso real contado pelo candidato.',
      },
      {
         key: 'competencia_tecnica',
         label: 'Competência técnica',
         hint: 'Domínio técnico exigido pela vaga, avaliado a partir das respostas.',
      },
      {
         key: 'fit_cultural',
         label: 'Fit cultural',
         hint: 'Aderência aos valores e ao jeito de trabalhar da empresa.',
      },
      { key: 'observacao_final', label: 'Observação final' },
   ],
};

export function structuredNotesFieldsForRound(round) {
   return STRUCTURED_NOTES_FIELDS_BY_ROUND[round] ?? [];
}

// ═══════════════════════════════════════════════════════════════════════════
// FASE 1 — Onboarding documental + conversão em funcionário (handoff 0065)
// ═══════════════════════════════════════════════════════════════════════════

// ─── Pipeline do onboarding (onboarding.status) ────────────────────────────
// Ordem F2 (handoffs 0066/0067): BACKGROUND_CHECK vem ANTES da coleta plena —
// reprovou nas análises, o checklist pleno nunca é gerado. Avança UM PASSO POR
// VEZ (backend recusa pulos: 422 invalid_onboarding_transition; sair do
// BACKGROUND_CHECK exige as 2 sub-análises APROVADO — 422
// background_check_not_approved). ADMITIDO só via ação ADMITIR (/admit);
// DESQUALIFICADO via /disqualify OU auto-desqualificação do backend (sub-
// análise REPROVADO no manual-steps → resposta 200 já DESQUALIFICADO).
export const ONBOARDING_STATUS = {
   BACKGROUND_CHECK: 'BACKGROUND_CHECK',
   COLETA_DOCS: 'COLETA_DOCS',
   PRE_CADASTRO: 'PRE_CADASTRO',
   EXAME_ADMISSIONAL: 'EXAME_ADMISSIONAL',
   RASCUNHO_ONVIO: 'RASCUNHO_ONVIO',
   ENVIO_CONTABILIDADE: 'ENVIO_CONTABILIDADE',
   AGUARDANDO_INICIO: 'AGUARDANDO_INICIO',
   ADMITIDO: 'ADMITIDO',
   DESQUALIFICADO: 'DESQUALIFICADO',
};

export const ONBOARDING_STATUS_LABEL = {
   BACKGROUND_CHECK: 'Background Check',
   COLETA_DOCS: 'Coleta de documentos',
   PRE_CADASTRO: 'Pré-cadastro',
   EXAME_ADMISSIONAL: 'Exame admissional',
   RASCUNHO_ONVIO: 'Rascunho Onvio',
   ENVIO_CONTABILIDADE: 'Envio à contabilidade',
   AGUARDANDO_INICIO: 'Aguardando início',
   ADMITIDO: 'Admitido',
   DESQUALIFICADO: 'Desqualificado',
};

// Trilha ordenada dos passos do pipeline (sem os terminais ADMITIDO/
// DESQUALIFICADO — a saída de AGUARDANDO_INICIO é a ação ADMITIR).
export const ONBOARDING_PIPELINE = [
   ONBOARDING_STATUS.BACKGROUND_CHECK,
   ONBOARDING_STATUS.COLETA_DOCS,
   ONBOARDING_STATUS.PRE_CADASTRO,
   ONBOARDING_STATUS.EXAME_ADMISSIONAL,
   ONBOARDING_STATUS.RASCUNHO_ONVIO,
   ONBOARDING_STATUS.ENVIO_CONTABILIDADE,
   ONBOARDING_STATUS.AGUARDANDO_INICIO,
];

export const ONBOARDING_TERMINAL_STATUSES = [
   ONBOARDING_STATUS.ADMITIDO,
   ONBOARDING_STATUS.DESQUALIFICADO,
];

export function isOnboardingTerminal(status) {
   return ONBOARDING_TERMINAL_STATUSES.includes(status);
}

// Próximo passo PATCH-ável do pipeline; null em AGUARDANDO_INICIO (a saída é
// ADMITIR) e nos terminais.
export function nextOnboardingStatus(status) {
   const index = ONBOARDING_PIPELINE.indexOf(status);
   if (index === -1) return null;
   return ONBOARDING_PIPELINE[index + 1] ?? null;
}

// Texto de apoio por etapa (o que o DP faz nela) — exibido no stepper.
export const ONBOARDING_STATUS_HINT = {
   BACKGROUND_CHECK:
      'Peça ao candidato um documento de identificação (RG ou CNH) para a checagem de histórico corporativo e jurídico, e registre as duas análises abaixo. Reprovação em qualquer uma desqualifica automaticamente.',
   COLETA_DOCS:
      'Background check aprovado: colete os demais documentos do checklist (arquivos e valores).',
   PRE_CADASTRO:
      'Preencha o pré-cadastro do funcionário (empresa, cargo, salário…).',
   EXAME_ADMISSIONAL:
      'Registre o resultado do exame admissional no painel da etapa.',
   RASCUNHO_ONVIO:
      'Monte o rascunho no Onvio e marque a caixa no painel da etapa.',
   ENVIO_CONTABILIDADE:
      'Envie a documentação à contabilidade e registre a data no painel da etapa.',
   AGUARDANDO_INICIO:
      'Tudo pronto: confirme a data de início no pré-cadastro e admita o funcionário.',
};

// ─── Passos manuais rastreados ─────────────────────────────────────────────
// Labels de resultado compartilhadas pelas 2 sub-análises e pelo agregado
// derivado `background_check_status` (read-only, calculado no backend).
export const BACKGROUND_CHECK_STATUS_LABEL = {
   PENDENTE: 'Pendente',
   APROVADO: 'Aprovado',
   REPROVADO: 'Reprovado',
};

export const BACKGROUND_CHECK_STATUS_OPTIONS = Object.keys(
   BACKGROUND_CHECK_STATUS_LABEL,
).map((value) => ({ value, label: BACKGROUND_CHECK_STATUS_LABEL[value] }));

// As 2 sub-análises do Background Check (handoff 0067 §1). REPROVADO em
// qualquer uma ⇒ auto-desqualificação atômica no backend.
export const BACKGROUND_CHECK_SUB_ANALYSES = [
   {
      statusKey: 'litigation_screening_status',
      notesKey: 'litigation_screening_notes',
      label: 'Triagem de Litígios e Compliance',
      shortLabel: 'Triagem de litígios',
   },
   {
      statusKey: 'professional_history_status',
      notesKey: 'professional_history_notes',
      label: 'Auditoria de Histórico Profissional',
      shortLabel: 'Histórico profissional',
   },
];

export const ADMISSION_EXAM_STATUS_LABEL = {
   PENDENTE: 'Pendente',
   APTO: 'Apto',
   INAPTO: 'Inapto',
};

export const ADMISSION_EXAM_STATUS_OPTIONS = Object.keys(
   ADMISSION_EXAM_STATUS_LABEL,
).map((value) => ({ value, label: ADMISSION_EXAM_STATUS_LABEL[value] }));

// ─── Pré-cadastro (dados do futuro funcionário) ────────────────────────────
export const MARITAL_STATUS_OPTIONS = [
   { value: 'SOLTEIRO', label: 'Solteiro(a)' },
   { value: 'CASADO', label: 'Casado(a)' },
   { value: 'DIVORCIADO', label: 'Divorciado(a)' },
   { value: 'VIUVO', label: 'Viúvo(a)' },
   { value: 'UNIAO_ESTAVEL', label: 'União estável' },
   { value: 'OUTRO', label: 'Outro' },
];

// Valores EXATOS do cadastro de funcionário (EmployeeForm) — o pré-cadastro
// espelha; mudar lá exige mudar aqui.
export const COMPANY_ENTITY_OPTIONS = [
   {
      value: 'MYSHOP COMERCIO E SERVICOS LTDA',
      label: 'MYSHOP COMERCIO E SERVICOS LTDA',
   },
   {
      value: 'LIFE PURIFICADORES LTDA',
      label: 'LIFE PURIFICADORES LTDA',
   },
];

export const TRANSPORTATION_COST_TYPE_OPTIONS = [
   { value: 'AUXÍLIO COMBUSTÍVEL', label: 'Auxílio combustível' },
   { value: 'VALE TRANSPORTE', label: 'Vale transporte' },
   { value: 'NENHUM', label: 'Nenhum' },
   { value: 'NÃO SE APLICA', label: 'Não se aplica' },
];

// Campos do rascunho exigidos pela ação ADMITIR (further: + start_date +
// candidate.cpf). Espelha a validação do backend (422 missing_fields).
export const ADMIT_REQUIRED_PRE_CADASTRO_FIELDS = [
   { key: 'company_entity', label: 'Empresa' },
   { key: 'job_position_id', label: 'Cargo' },
   { key: 'department_id', label: 'Departamento' },
   { key: 'cost_center_id', label: 'Centro de custo' },
   { key: 'base_salary', label: 'Salário base' },
];

// Campos "contáveis" do pré-cadastro (valor, não checkbox) — alimentam a meta
// "Rascunho · N de 14" do accordion e o rail de progresso.
export const PRE_CADASTRO_COUNTABLE_FIELDS = [
   'marital_status',
   'company_entity',
   'start_date',
   'job_position_id',
   'department_id',
   'cost_center_id',
   'base_salary',
   'monthly_work_hours',
   'transportation_cost_type',
   'city_location',
   'pix_key',
   'whatsapp_number',
   'email',
   'responsible_hr_user_id',
];

export function countPreCadastroFilled(onboarding) {
   return PRE_CADASTRO_COUNTABLE_FIELDS.filter((key) => {
      const value = onboarding?.[key];
      return value !== null && value !== undefined && value !== '';
   }).length;
}

// "Completo" pro ADMITIR: os 5 obrigatórios + data de início.
export function isPreCadastroComplete(onboarding) {
   const required = [
      ...ADMIT_REQUIRED_PRE_CADASTRO_FIELDS.map((field) => field.key),
      'start_date',
   ];
   return required.every((key) => {
      const value = onboarding?.[key];
      return value !== null && value !== undefined && value !== '';
   });
}

// ─── Dependentes ───────────────────────────────────────────────────────────
export const DEPENDENT_RELATIONSHIP_LABEL = {
   FILHO: 'Filho',
   FILHA: 'Filha',
   OUTRO: 'Outro',
};

export const DEPENDENT_RELATIONSHIP_OPTIONS = Object.keys(
   DEPENDENT_RELATIONSHIP_LABEL,
).map((value) => ({ value, label: DEPENDENT_RELATIONSHIP_LABEL[value] }));

// ─── Checklist de documentos de admissão ───────────────────────────────────
export const ADMISSION_DOCUMENT_STATUS = {
   PENDENTE: 'PENDENTE',
   RECEBIDO: 'RECEBIDO',
   ENVIADO_CONTABILIDADE: 'ENVIADO_CONTABILIDADE',
   NAO_APLICA: 'NAO_APLICA',
};

export const ADMISSION_DOCUMENT_STATUS_LABEL = {
   PENDENTE: 'Pendente',
   RECEBIDO: 'Recebido',
   ENVIADO_CONTABILIDADE: 'Enviado à contabilidade',
   NAO_APLICA: 'Não se aplica',
};

// document_type.collection_kind — FILE = upload de arquivo; VALUE = valor
// textual (ex.: nº da CTPS).
export const COLLECTION_KIND = {
   FILE: 'FILE',
   VALUE: 'VALUE',
};

// Documentos MÍNIMOS OBRIGATÓRIOS do cadastro de funcionário (pedido
// 2026-07-15): não podem ser marcados "Não se aplica" e aparecem NO TOPO do
// checklist, NESTA ordem (hierarquia visual). Match primário por `code` do
// catálogo (seed do backend) com fallback por `name` — só o
// IDENTIFICACAO_RG_CNH é confirmado pelo handoff 0067; ajustar os demais se o
// seed divergir.
const MANDATORY_ADMISSION_DOCS = [
   {
      codes: ['IDENTIFICACAO_RG_CNH'],
      name: 'Documento de identificação (RG ou CNH)',
   },
   { codes: ['CPF'], name: 'CPF' },
   { codes: ['NUMERO_CTPS', 'CTPS_NUMERO'], name: 'Número da CTPS' },
   { codes: ['TITULO_ELEITOR'], name: 'Título de Eleitor' },
   {
      codes: ['COMPROVANTE_ENDERECO', 'COMPROVANTE_ENDERECO_CEP'],
      name: 'Comprovante de Endereço com CEP',
   },
];

// Posição do documento na ordem canônica dos obrigatórios; -1 = não é
// obrigatório (opcional/condicional — vai pro fim, na ordem do backend).
export function mandatoryAdmissionDocRank(documentType) {
   if (!documentType) return -1;
   return MANDATORY_ADMISSION_DOCS.findIndex(
      (entry) =>
         entry.codes.includes(documentType.code) ||
         entry.name === documentType.name,
   );
}

export function isMandatoryAdmissionDoc(documentType) {
   return mandatoryAdmissionDocRank(documentType) !== -1;
}

// ─── Requisitos por cargo (§8) ─────────────────────────────────────────────
// Só DOCUMENT dirige o checklist hoje; os demais são fundação.
export const REQUIREMENT_TYPE_LABEL = {
   DOCUMENT: 'Documento',
   COURSE: 'Curso',
   TECHNICAL_EXPERIENCE: 'Experiência técnica',
   SKILL: 'Habilidade',
   OTHER: 'Outro',
};

export const REQUIREMENT_TYPE_OPTIONS = Object.keys(REQUIREMENT_TYPE_LABEL).map(
   (value) => ({ value, label: REQUIREMENT_TYPE_LABEL[value] }),
);
